import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
  keepPreviousData,
} from '@tanstack/react-query';
import { STALE_TIMES, GC_TIMES } from './constants';

export interface QueryKeyMap {
  all: readonly string[];
  list: (params?: unknown) => readonly unknown[];
  detail: (id: string) => readonly unknown[];
}

interface CreateQueryHooksOptions<TItem, TCreate, TUpdate, TList, TListResult> {
  resourceKey: string;
  endpoints: {
    list?: (params?: TList) => Promise<TListResult>;
    getById?: (id: string) => Promise<TItem>;
    create?: (params: TCreate) => Promise<TItem>;
    update?: (id: string, params: TUpdate) => Promise<TItem>;
    delete?: (id: string) => Promise<void>;
  };
  staleTime?: number;
  gcTime?: number;
}

export function createQueryHooks<
  TItem = unknown,
  TCreate = unknown,
  TUpdate = unknown,
  TList = unknown,
  TListResult = unknown,
>(options: CreateQueryHooksOptions<TItem, TCreate, TUpdate, TList, TListResult>) {
  const { resourceKey, endpoints, staleTime, gcTime } = options;

  const defaultStaleTime = staleTime ?? STALE_TIMES[resourceKey as keyof typeof STALE_TIMES] ?? 30_000;
  const defaultGcTime = gcTime ?? GC_TIMES[resourceKey as keyof typeof GC_TIMES] ?? 5 * 60_000;

  const queryKeys: QueryKeyMap = {
    all: [resourceKey] as const,
    list: (params?: unknown) => [resourceKey, 'list', params] as const,
    detail: (id: string) => [resourceKey, 'detail', id] as const,
  };

  function useList(params?: TList): UseQueryResult<TListResult> {
    return useQuery({
      queryKey: queryKeys.list(params),
      queryFn: () => endpoints.list!(params),
      staleTime: defaultStaleTime,
      gcTime: defaultGcTime,
      enabled: !!endpoints.list,
      // keepPreviousData: preserves previous page data during filter/search/param changes
      // (smooth UX, no empty flash while refetching). See perf plan.
      placeholderData: keepPreviousData,
      // Note: light field projection ready in endpoint signatures (e.g. listServers fields?: string[])
      // can be passed via listParams for future backend SELECT optimization (non-breaking).
    });
  }

  function useDetail(id: string): UseQueryResult<TItem> {
    return useQuery({
      queryKey: queryKeys.detail(id),
      queryFn: () => endpoints.getById!(id),
      staleTime: defaultStaleTime,
      gcTime: defaultGcTime,
      enabled: !!id && !!endpoints.getById,
    });
  }

  function useCreate(): UseMutationResult<TItem, Error, TCreate> {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (params: TCreate) => endpoints.create!(params),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.all });
      },
    });
  }

  function useUpdate(): UseMutationResult<TItem, Error, { id: string; params: TUpdate }> {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({ id, params }: { id: string; params: TUpdate }) =>
        endpoints.update!(id, params),
      onSuccess: (_data, { id }) => {
        queryClient.invalidateQueries({ queryKey: queryKeys.detail(id) });
        queryClient.invalidateQueries({ queryKey: queryKeys.all });
      },
    });
  }

  function useDelete(): UseMutationResult<void, Error, string> {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (id: string) => endpoints.delete!(id),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.all });
      },
    });
  }

  return { queryKeys, useList, useDetail, useCreate, useUpdate, useDelete };
}
